import { Avatar, AvatarGroup, Box, Flex, forwardRef, Heading, HTMLChakraProps, Text } from '@chakra-ui/react';
import Image from 'next/image';
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LectureCardTag } from '@/components/ui/shared/lecture/LectureCard/LectureCardTags/LectureCardTags';
import { LectureCardImage } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardImage';
import { ThinEvent } from '@/resources/ThinEvent';
import { ThinLecture } from '@/resources/ThinLecture';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';
import { dateToString } from '@/utils/date';

export interface IThinLectureProps extends HTMLChakraProps<'div'> {
  lecture: ThinLecture;
  icon?: ReactNode;
  indicator?: ReactNode;
  event?: ThinEvent;
}

export const ThinLectureCard = forwardRef<IThinLectureProps, 'div'>(
  ({ lecture, indicator, event, icon, ...rest }, ref) => {
    const { title: name, posterThumbnailUrl, tag, sortedProfiles, heldAt } = lecture;

    const { t } = useTranslation();

    return (
      <Flex flexDir="column" _hover={{ textDecor: 'none' }} ref={ref} {...rest}>
        <LectureCardImage src={posterThumbnailUrl || LECTURE_CARD_IMAGE_FALLBACK} alt={name} />
        <Flex pos="relative" flexDir="column" flex={1} p="4" backgroundColor="white">
          <Flex justifyContent="space-between">
            <LectureCardTag tagItem={tag} />
            <Box justifySelf="flex-end">{icon}</Box>
          </Flex>
          <Heading as="h3" mb="auto" noOfLines={3} fontSize="md" lineHeight="base">
            {name}
          </Heading>
          <Text mt={2} textTransform="uppercase" fontSize="xs" lineHeight="shorter" color="gray.800">
            {event.name}
          </Text>
          {indicator}
          {sortedProfiles?.length > 0 ? (
            <Box mt="4">
              <AvatarGroup max={3} spacing="-1" size="xs" mb="2">
                {sortedProfiles?.map(({ id, fullName, mediumSquareAvatarUrl }) => (
                  <Fragment key={id}>
                    {mediumSquareAvatarUrl && (
                      <Image
                        src={mediumSquareAvatarUrl}
                        alt={fullName}
                        width={28}
                        height={28}
                        style={{ borderRadius: '50%', width: '28px', height: '28px', objectFit: 'cover' }}
                      />
                    )}
                    {!mediumSquareAvatarUrl && (
                      <Avatar
                        backgroundColor="violet.500"
                        color="white"
                        size="xs"
                        key={id}
                        name={fullName}
                        src={mediumSquareAvatarUrl}
                      />
                    )}
                  </Fragment>
                ))}
              </AvatarGroup>
              <Flex minW="0">
                <Text as="span" noOfLines={1} color="gray.800" fontSize="sm" lineHeight="base">
                  {sortedProfiles[0].fullName}
                </Text>
                {sortedProfiles?.length > 1 ? (
                  <Fragment>
                    &nbsp;
                    <Text as="span" flexShrink={0} color="gray.800" fontSize="sm" lineHeight="base">
                      {t('library.lecture.authors', { count: sortedProfiles.length - 1 })}
                    </Text>
                  </Fragment>
                ) : null}
              </Flex>
            </Box>
          ) : null}
          {heldAt && (
            <Text mt={3} color="gray.800" fontSize="sm" lineHeight="base">
              {dateToString(heldAt, 'dd MMMM yyyy')}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }
);
